import { eq } from "drizzle-orm";
import { ingredient, type PowerSyncDrizzleDB, recipe_item } from "@comandero/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

/**
 * Crea un ingrediente con stock inicial (entero). El stock es el SALDO DERIVADO
 * que el server descuenta al cobrar órdenes; el cliente lo lee, no lo recalcula
 * por venta (evita doble descuento entre terminales).
 */
export async function createIngredientMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  opts?: { unit?: string; stock?: number; cost?: number },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(ingredient).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    unit: opts?.unit ?? "unit",
    stock: Math.trunc(opts?.stock ?? 0),
    cost: Math.max(0, Math.trunc(opts?.cost ?? 0)),
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Ajusta el costo (centavos por unidad de stock) de un ingrediente. */
export async function setIngredientCostMutation(
  db: PowerSyncDrizzleDB,
  ingredientId: string,
  cost: number,
): Promise<void> {
  await db
    .update(ingredient)
    .set({ cost: Math.max(0, Math.trunc(cost)), updated_at: nowISO() })
    .where(eq(ingredient.id, ingredientId));
}

/** Ajuste manual del stock de un ingrediente (conteo físico, merma, reposición). */
export async function setIngredientStockMutation(
  db: PowerSyncDrizzleDB,
  ingredientId: string,
  stock: number,
): Promise<void> {
  await db
    .update(ingredient)
    .set({ stock: Math.trunc(stock), updated_at: nowISO() })
    .where(eq(ingredient.id, ingredientId));
}

export async function deleteIngredientMutation(
  db: PowerSyncDrizzleDB,
  ingredientId: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(ingredient)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(ingredient.id, ingredientId));
}

/**
 * Agrega (o actualiza) una línea de receta: el producto `productId` consume
 * `qty` unidades del ingrediente `ingredientId` por cada unidad vendida.
 */
export async function setRecipeItemMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  productId: string,
  ingredientId: string,
  qty: number,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(recipe_item).values({
    id,
    organization_id: organizationId,
    product_id: productId,
    ingredient_id: ingredientId,
    qty: Math.max(0, Math.trunc(qty)),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteRecipeItemMutation(
  db: PowerSyncDrizzleDB,
  recipeItemId: string,
): Promise<void> {
  await db.delete(recipe_item).where(eq(recipe_item.id, recipeItemId));
}
